import React from 'react'
import moment from 'moment'

// @src imports
import { Button, RwdCell, RwdRow } from 'src/chrome'
import { AddOrderCardRoute } from 'src/orders/routes/AddOrderCardRoute'

import { InviteFragment, SocProContactFragment } from 'src/graphql'

import styles from './invites.module.scss'
import { Icon, Logo, Text } from '@sendoutcards/quantum-design-ui'
import uuid from 'src/utils/uuid'
import { formatLocation } from 'src/helpers'
import { useActions, useMutations } from 'src/hooks'

interface ProspectProps {
  index: number
  invite: InviteFragment
}

const convertStreamToBrandIcon = (stream: string | null) => {
  if (!stream || stream === '') {
    return null
  }

  switch (stream) {
    case 'blue42':
      return <Logo brand="blue42" color="color" />

    case 'greenerStill':
      return <Logo brand="greenerStill" color="color" />

    case 'bellaCadeaux':
      return <Logo brand="bellaCadeaux" color="color" />

    case 'promptingU':
      return <Logo brand="promptingU" color="color" />

    case 'soc':
      return <Logo brand="soc" color="color" />

    case 'streamMarketing':
      return <Logo brand="streamMarketing" color="color" />

    case 'streamVA':
      return <Logo brand="streamVA" color="color" />

    default:
      return null
  }
}

const Prospect: React.FC<ProspectProps> = props => {
  const { index, invite } = props
  const { createdAt, prospect, socProContact } = invite

  const mutations = useMutations()
  const actions = useActions()

  const firstName = prospect?.firstName ?? socProContact?.firstName ?? ''
  const lastName = prospect?.lastName ?? socProContact?.lastName ?? ''
  const phoneNumber =
    prospect?.phoneNumber ??
    socProContact?.homePhone ??
    socProContact?.smsPhone ??
    socProContact?.workPhone ??
    ''
  const email = prospect?.email ?? socProContact?.email ?? ''
  const address1 = prospect?.address?.address1 ?? socProContact?.address ?? ''
  const address2 = prospect?.address?.address2 ?? socProContact?.address2 ?? ''
  const cityAndState = formatLocation(
    prospect?.address?.city ?? socProContact?.city,
    prospect?.address?.state ?? socProContact?.state,
    '',
  )
  const location = [
    cityAndState,
    prospect?.address?.postalCode ?? socProContact?.zip,
  ].join(' ')
  const streamIcon = convertStreamToBrandIcon(invite.utmStream)
  const activityIcons = [
    invite.hasProspectJoined && (
      <Icon name="star" size="small" primaryColor="primaryBrand" />
    ),
    invite.hasSentCard && (
      <Icon name="envelope" size="small" primaryColor="primaryBrand" />
    ),
  ]

  const onSendCard = async (socProContact: SocProContactFragment) => {
    if (
      !socProContact.address ||
      !socProContact.city ||
      !socProContact.state ||
      !socProContact.country
    ) {
      // TODO: prompt an error dialog to the user
      console.log('Incomplete address!')
      return
    }

    const contactId = uuid()
    // await for the result to prevent race conditions
    await mutations.createContacts({
      contacts: [
        {
          id: contactId,
          firstName: socProContact.firstName ?? '',
          lastName: socProContact.lastName ?? '',
          address1: socProContact.address ?? '',
          address2: socProContact.address2 ?? '',
          city: socProContact.city ?? '',
          state: socProContact.state ?? '',
          country: socProContact.country ?? '',
          postalCode: socProContact.zip ?? '',
        },
      ],
    })

    const {
      createOrder: { order },
    } = await mutations.createOrder({ order: { contacts: [contactId] } })

    actions.openOrder(order.id, AddOrderCardRoute())
  }

  return (
    <React.Fragment>
      <RwdRow id={`invite_${index}`}>
        <RwdCell mapToHeader="Name">
          <Text type="body" color="primaryHeading">
            {firstName} {lastName}
          </Text>
        </RwdCell>
        <RwdCell mapToHeader="Date">
          <Text type="body" color="primaryHeading">
            {moment(createdAt).format('MM/DD/YY')}
          </Text>
        </RwdCell>
        <RwdCell mapToHeader="Phone">
          <Text type="body" color="primaryHeading">
            {phoneNumber}
          </Text>
        </RwdCell>
        <RwdCell mapToHeader="Email">
          <Text type="body" color="primaryHeading">
            {email}
          </Text>
        </RwdCell>
        <RwdCell mapToHeader="Address">
          <Text type="caption">{address1}</Text>
          <Text type="caption">{address2}</Text>
          <Text type="caption">{location}</Text>
          <Text type="caption">{prospect?.address?.country}</Text>
        </RwdCell>
        <RwdCell mapToHeader="Stream">{streamIcon}</RwdCell>
        <RwdCell mapToHeader="Activity">{activityIcons}</RwdCell>
        <RwdCell mapToHeader="actions">
          {socProContact && socProContact.contactId && (
            <Button
              title={'Send a Card'}
              buttonColor={'pink'}
              className={styles.sendButton}
              onClick={() => onSendCard(socProContact)}
            />
          )}
        </RwdCell>
      </RwdRow>
      <tr className={styles.tableSpacer} />
    </React.Fragment>
  )
}

export default Prospect
